import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Grid,
  Header,
  Icon,
  Card,
  Modal,
  Select,
  Checkbox,
  Message,
  Form,
  Segment,
  Label,
  Divider,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import axios from "axios";
import { buildApiUrl } from "../utils/apiConfig";
import { getEffectivePortCoordinates } from "../utils/tourPortLocations";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SidebarWrapper from './SidebarWrapper';
import { useOptionalMapContext } from "./MapContext";

const TEMPLATE_EVENT_ROUTE_OPTIONS = [
  { key: "road", value: "road", text: "Road" },
  { key: "river", value: "river", text: "River" },
  { key: "sea", value: "sea", text: "Sea" },
];

const TEMPLATE_SEGMENT_ROUTE_OPTIONS = [
  { key: "auto", value: "", text: "Auto" },
  { key: "road", value: "road", text: "Road" },
  { key: "river", value: "river", text: "River" },
  { key: "sea", value: "sea", text: "Sea" },
];

const TEMPLATE_EVENT_LOCATION_MODE_OPTIONS = [
  { key: "port", value: "port", text: "At port" },
  { key: "nearby_port", value: "nearby_port", text: "Nearby city" },
  { key: "custom", value: "custom", text: "Map / custom coordinate" },
];

const templateEventKey = (portIndex, eventIndex) => `${portIndex}:${eventIndex}`;

const isValidLonLat = (lon, lat) =>
  Number.isFinite(lon) &&
  Number.isFinite(lat) &&
  lon >= -180 &&
  lon <= 180 &&
  lat >= -90 &&
  lat <= 90;

const normalizeTemplateSegmentRouteMode = (value) =>
  ["road", "river", "sea"].includes(value) ? value : "";

const geoJsonPoint = (lon, lat) => ({
  type: "Point",
  coordinates: [Number(lon), Number(lat)],
});

const getStartOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const normalizeTemplateEvent = (event = {}, fallback = {}) => ({
  id: event.id || null,
  name: event.name || "",
  description: event.description || "",
  day_number: event.day_number ?? fallback.day_number ?? "",
  duration: event.duration || "",
  placement: event.placement === "segment" ? "segment" : "port",
  segment_end_port_of_call_id: event.segment_end_port_of_call_id || null,
  route_mode: event.route_mode || "",
  segment_route_behavior: event.segment_route_behavior === "separate" ? "separate" : "waypoint",
  display_as_port_card: Boolean(event.display_as_port_card),
  custom_location: event.custom_location || event.location || null,
  custom_location_label: event.custom_location_label || "",
  location_country_iso3:
    event.location_country?.iso3_code ||
    event.location_country_iso3 ||
    fallback.location_country_iso3 ||
    "",
  location_country_touched: Boolean(event.location_country_touched),
  location_mode: event.location_mode || (event.custom_location || event.location ? "custom" : "port"),
  nearby_port_of_call_id: event.nearby_port_of_call_id || null,
});

export default function TourListBar({
  // map, // kept for compatibility; not used
  activeUser,
  // mapBuildings, // kept for compatibility; not used
  userTours,
  setUserTours,
  setActiveTourToShow,
  setShowTourListBar,
  selectTourAndOpenDetail, // Function to select tour and open detail bar
  setActiveMapToEdit,
  fetchFullMapData,
  getApiHeaders,
  isMobile,
  isUserSwitching = false,
  sidebarConfig, // Add sidebar configuration
  onHeightChange, // Callback to notify MainMap of height changes
  onWideSidebarChange,
}) {
  const mapCtx = useOptionalMapContext();

  const [open, setOpen] = useState(false);
  const [listOfTourTempl, setListOfTourTempl] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [currentDate, setNewDate] = useState(null);
  const [newTourError, setNewTourError] = useState(null);
  const [isCreatingTour, setIsCreatingTour] = useState(false);
  const onDateChange = (date) => {
    setNewTourError(null);
    setNewDate(date);
  };
  const [activeTab, setActiveTab] = useState("current");
  const [activeView, setActiveView] = useState("tours");

  // Template management state
  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState(null);
  const [templatePorts, setTemplatePorts] = useState([]);
  const [templateMessage, setTemplateMessage] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [portsCatalog, setPortsCatalog] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tourTypes, setTourTypes] = useState([]);
  const [selectedPortToAdd, setSelectedPortToAdd] = useState(null);
  const [isLoadingTemplateMap, setIsLoadingTemplateMap] = useState(false);
  const [activeTemplateEventMapKey, setActiveTemplateEventMapKey] = useState(null);
  const [templateEventCountryLookupKey, setTemplateEventCountryLookupKey] = useState(null);

  // Flight tracking states
  const [flightTrackingModalOpen, setFlightTrackingModalOpen] = useState(false);
  const [selectedTourForTracking, setSelectedTourForTracking] = useState(null);
  const [homeAirport, setHomeAirport] = useState(null);
  const [outboundAirports, setOutboundAirports] = useState([]);
  const [returnAirports, setReturnAirports] = useState([]);
  const [selectedOutboundAirports, setSelectedOutboundAirports] = useState([]);
  const [selectedReturnAirports, setSelectedReturnAirports] = useState([]);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [trackingMessage, setTrackingMessage] = useState(null);
  const [trackingError, setTrackingError] = useState(null);

  const getTourDateRange = (tour) => {
    const startDate = tour?.start_date ? new Date(tour.start_date) : null;
    if (!startDate || Number.isNaN(startDate.getTime())) {
      return { startDate: null, endDate: null };
    }

    const endDate = new Date(startDate);
    const durationDays = Number(tour?.duration || 1);
    endDate.setDate(endDate.getDate() + Math.max(durationDays, 1) - 1);
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const getOrderedPortsWithLocation = (ports) =>
    (ports || [])
      .filter((port) => getEffectivePortCoordinates(port)?.length === 2)
      .slice()
      .sort((a, b) => (a?.visit_order ?? 0) - (b?.visit_order ?? 0));

  const openTour = (id) => {
    if (isUserSwitching) return;
    const tour = userTours.find((t) => t.id === id);
    if (selectTourAndOpenDetail) {
      selectTourAndOpenDetail(tour);
    } else {
      setActiveTourToShow(tour);
    }
  };

  const closeTourListBar = () => setShowTourListBar(false);

  const closeNewTourModal = () => {
    if (isCreatingTour) return;
    setOpen(false);
    setNewTourError(null);
  };

  const openNewTourModal = () => {
    setNewTourError(null);
    setNewDate((existingDate) => existingDate || getStartOfToday());
    setPickedTemplate((existingTemplate) => {
      if (existingTemplate) return existingTemplate;
      if (templates.length === 1) return templates[0].id;
      if (listOfTourTempl.length === 1) return listOfTourTempl[0].value;
      return null;
    });
    setOpen(true);
  };

  const addNewTour = async () => {
    if (!activeUser?.object_id) {
      setNewTourError("User data is still loading. Please wait a moment and try again.");
      return;
    }
    if (!pickedTemplate) {
      setNewTourError("Select the itinerary template for this sailing.");
      return;
    }
    if (!currentDate || Number.isNaN(currentDate.getTime())) {
      setNewTourError("Select the embarkation date for this tour.");
      return;
    }

    setIsCreatingTour(true);
    setNewTourError(null);
    try {
      const newTour = await axios({
        url: buildApiUrl("tour/"),
        method: "post",
        data: {
          template_id: pickedTemplate,
          start_date:
            currentDate.toLocaleDateString("fr-CA", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }) + " 12:00",
          user_id: activeUser.object_id,
        },
        headers: await getApiHeaders(),
      });
      setUserTours((previousTours) => [...(previousTours || []), newTour.data]);
      setOpen(false);
      if (selectTourAndOpenDetail) {
        selectTourAndOpenDetail(newTour.data);
      } else {
        setActiveTourToShow(newTour.data);
      }
    } catch (err) {
      console.error(err);
      setNewTourError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Failed to create the tour. Please check the template and try again."
      );
    } finally {
      setIsCreatingTour(false);
    }
  };

  // Fetch user's home airport from preferences
  useEffect(() => {
    const fetchUserPreferences = async () => {
      const headers = await getApiHeaders();
      if (!headers.Authorization) return;

      try {
        const response = await axios({
          url: buildApiUrl('user-preferences/me/'),
          method: 'GET',
          headers
        });

        if (response.data.preferences?.home_airport) {
          setHomeAirport(response.data.preferences.home_airport);
        }
      } catch (err) {
        console.error('Error fetching user preferences:', err);
      }
    };

    fetchUserPreferences();
  }, [getApiHeaders]);

  const fetchTemplateOptions = async () => {
    try {
      const tourTempl = await axios({
        url: buildApiUrl("tour_template_basic/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      setListOfTourTempl(
        (tourTempl.data || []).map((el) => ({
          key: el.id,
          value: el.id,
          text: el.name,
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setTemplateError(null);
    try {
      const response = await axios({
        url: buildApiUrl("tour_template_lite/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      setTemplates(response.data || []);
    } catch (err) {
      console.error(err);
      setTemplateError("Failed to load templates.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchPortsCatalog = async () => {
    try {
      const response = await axios({
        url: buildApiUrl("ports_of_call_lite/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      setPortsCatalog(response.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTourTypes = async () => {
    try {
      const response = await axios({
        url: buildApiUrl("tour_types/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      setTourTypes(response.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios({
        url: buildApiUrl("countries/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      const countryRows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
          ? response.data.results
          : [];
      setCountries(countryRows);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const initData = async () => {
      const headers = await getApiHeaders();
      if (!headers.Authorization) return;
      fetchTemplateOptions();
      fetchTemplates();
      fetchPortsCatalog();
      fetchCountries();
      fetchTourTypes();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getApiHeaders]);

  const openFlightTrackingModal = async (tour) => {
    setSelectedTourForTracking(tour);
    setTrackingMessage(null);
    setTrackingError(null);
    setSelectedOutboundAirports([]);
    setSelectedReturnAirports([]);
    setFlightTrackingModalOpen(true);

    // Fetch nearest airports for the tour
    await fetchNearestAirports(tour);
  };

  const fetchNearestAirports = async (tour) => {
    setLoadingAirports(true);
    setTrackingError(null);

    try {
      // Get tour details with ports
      const tourResponse = await axios({
        url: buildApiUrl(`tour/${tour.id}/`),
        method: 'GET',
        headers: await getApiHeaders()
      });

      const ports = getOrderedPortsWithLocation(tourResponse.data.ports_of_call);

      if (!ports || ports.length === 0) {
        setTrackingError('This tour has no ports of call defined.');
        setLoadingAirports(false);
        return;
      }

      // Get first and last port coordinates
      const firstPort = ports[0];
      const lastPort = ports[ports.length - 1];
      const firstPortCoords = getEffectivePortCoordinates(firstPort);
      const lastPortCoords = getEffectivePortCoordinates(lastPort);

      // Fetch nearest airports for first port (outbound)
      if (firstPortCoords) {
        const outboundResponse = await axios({
          url: buildApiUrl(`nearest-airports/?latitude=${firstPortCoords[1]}&longitude=${firstPortCoords[0]}&limit=5`),
          method: 'GET',
          headers: await getApiHeaders()
        });
        setOutboundAirports(outboundResponse.data.airports || []);
      }

      // Fetch nearest airports for last port (return)
      if (lastPortCoords) {
        const returnResponse = await axios({
          url: buildApiUrl(`nearest-airports/?latitude=${lastPortCoords[1]}&longitude=${lastPortCoords[0]}&limit=5`),
          method: 'GET',
          headers: await getApiHeaders()
        });
        setReturnAirports(returnResponse.data.airports || []);
      }

    } catch (err) {
      console.error('Error fetching nearest airports:', err);
      setTrackingError('Failed to fetch nearest airports. Please try again.');
    } finally {
      setLoadingAirports(false);
    }
  };

  const setupFlightTracking = async () => {
    if (!homeAirport) {
      setTrackingError('Please set your home airport in user preferences first.');
      return;
    }

    if (selectedOutboundAirports.length === 0 && selectedReturnAirports.length === 0) {
      setTrackingError('Please select at least one outbound or return airport.');
      return;
    }

    setLoadingAirports(true);
    setTrackingError(null);
    setTrackingMessage(null);

    try {
      const tourDate = new Date(selectedTourForTracking.start_date);
      const departureDateStr = new Date(tourDate.getTime() - 86400000).toISOString().split('T')[0]; // Day before tour
      const returnDateStr = new Date(tourDate.getTime() + (selectedTourForTracking.duration * 86400000)).toISOString().split('T')[0]; // Last day of tour

      const response = await axios({
        url: buildApiUrl('flight-routes/setup-tracking/'),
        method: 'POST',
        headers: {
          ...(await getApiHeaders()),
          'Content-Type': 'application/json'
        },
        data: {
          tour_id: selectedTourForTracking.id,
          home_airport_code: homeAirport.code,
          departure_date: departureDateStr,
          return_date: returnDateStr,
          destination_airport_codes: selectedOutboundAirports,
          return_origin_airport_codes: selectedReturnAirports
        }
      });

      setTrackingMessage(`Successfully created ${response.data.routes.length} flight tracking route(s)!`);

      // Close modal after 2 seconds
      setTimeout(() => {
        setFlightTrackingModalOpen(false);
      }, 2000);

    } catch (err) {
      console.error('Error setting up flight tracking:', err);
      setTrackingError(err.response?.data?.error || 'Failed to set up flight tracking. Please try again.');
    } finally {
      setLoadingAirports(false);
    }
  };

  const today = getStartOfToday();

  const toursToShow = (userTours || []).filter((tour) => {
    const { endDate } = getTourDateRange(tour);
    if (!endDate) {
      return activeTab === "current";
    }

    if (activeTab === "current") {
      return endDate >= today;
    }
    if (activeTab === "historic") {
      return endDate < today;
    }
    return true;
  });

  const cards = [];
  if (toursToShow.length) {
    toursToShow
      .slice()
      .sort((a, b) => {
        const aStart = getTourDateRange(a).startDate;
        const bStart = getTourDateRange(b).startDate;
        const aTime = aStart ? aStart.getTime() : 0;
        const bTime = bStart ? bStart.getTime() : 0;
        return activeTab === "historic" ? bTime - aTime : aTime - bTime;
      })
      .forEach((oneTour) => {
        const { startDate, endDate } = getTourDateRange(oneTour);
        const date = startDate
          ? startDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "Date TBD";
        const endDateLabel =
          startDate && endDate && startDate.toDateString() !== endDate.toDateString()
            ? ` - ${endDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}`
            : "";
        const flightCount = oneTour.flight_route_count || 0;
        const dateLabel = `${date}${endDateLabel}`;
        const metaText = flightCount > 0 ? `${dateLabel} • ${flightCount} flight${flightCount !== 1 ? 's' : ''} tracked` : dateLabel;
        cards.push({
          key: oneTour.id,
          meta: metaText,
          header: oneTour.name,
          description: "",
          tour: oneTour,
        });
      });
  }

  const showTemplateEditor = Boolean(templateForm);

  useEffect(() => {
    if (!showTemplateEditor) {
      mapCtx?.tempPoint?.remove?.();
      setActiveTemplateEventMapKey(null);
    }
  }, [mapCtx?.tempPoint, showTemplateEditor]);

  useEffect(() => {
    onWideSidebarChange?.(Boolean(showTemplateEditor));
    return () => onWideSidebarChange?.(false);
  }, [onWideSidebarChange, showTemplateEditor]);

  const resetTemplateForm = (keepFeedback = false) => {
    mapCtx?.tempPoint?.remove?.();
    setActiveTemplateEventMapKey(null);
    setTemplateForm(null);
    setTemplatePorts([]);
    setSelectedPortToAdd(null);
    if (!keepFeedback) {
      setTemplateMessage(null);
      setTemplateError(null);
    }
  };

  const startNewTemplate = () => {
    setTemplateForm({
      id: null,
      name: "",
      code: "",
      duration: "",
      description: "",
      tour_type_id: null,
      tour_map: null,
    });
    setTemplatePorts([]);
    setSelectedPortToAdd(null);
    setTemplateMessage(null);
    setTemplateError(null);
  };

  const normalizeTemplatePorts = (ports) =>
    ports.map((port, index, allPorts) => ({
      ...port,
      route_to_next_mode:
        index === allPorts.length - 1
          ? ""
          : normalizeTemplateSegmentRouteMode(port.route_to_next_mode),
      events: (port.events || []).map((event) => normalizeTemplateEvent(event)),
      port_order: index + 1,
    }));

  const reconcileTemplatePortEvents = (ports) =>
    normalizeTemplatePorts(ports).map((port, index, normalizedPorts) => {
      const nextPort = normalizedPorts[index + 1] || null;
      return {
        ...port,
        events: (port.events || []).map((event) => {
          const normalizedEvent = normalizeTemplateEvent(event, {
            location_country_iso3: port.port_of_call?.country?.iso3_code || "",
          });
          if (normalizedEvent.placement === "segment") {
            if (nextPort?.port_of_call_id) {
              normalizedEvent.segment_end_port_of_call_id = nextPort.port_of_call_id;
            } else {
              normalizedEvent.placement = "port";
              normalizedEvent.segment_end_port_of_call_id = null;
              normalizedEvent.segment_route_behavior = "waypoint";
            }
          } else {
            normalizedEvent.segment_end_port_of_call_id = null;
            normalizedEvent.segment_route_behavior = "waypoint";
          }
          return normalizedEvent;
        }),
      };
    });

  const selectTemplateForEdit = (template) => {
    const loadTemplate = async () => {
      setTemplatesLoading(true);
      setTemplateError(null);
      try {
        const response = await axios({
          url: buildApiUrl(`tour_template/${template.id}/`),
          method: "get",
          headers: await getApiHeaders(),
        });
        const fullTemplate = response.data;
        setTemplateForm({
          id: fullTemplate.id,
          name: fullTemplate.name || "",
          code: fullTemplate.code || "",
          duration: fullTemplate.duration ?? "",
          description: fullTemplate.description || "",
          tour_type_id: fullTemplate.tour_type?.id || fullTemplate.tour_type || null,
          tour_map: fullTemplate.tour_map || null,
        });
        const orderedPorts = (fullTemplate.port_orders || [])
          .slice()
          .sort((a, b) => (a.port_order ?? 0) - (b.port_order ?? 0))
          .map((order) => ({
            id: order.id,
            port_of_call_id: order.port_of_call?.id,
            port_of_call: order.port_of_call,
            port_order: order.port_order,
            day_arrival: order.day_arrival ?? "",
            day_departure: order.day_departure ?? "",
            route_to_next_mode: order.route_to_next_mode || "",
            events: (order.events || []).map((event) =>
              normalizeTemplateEvent(event, {
                location_country_iso3: order.port_of_call?.country?.iso3_code || "",
              })
            ),
          }));
        setTemplatePorts(reconcileTemplatePortEvents(orderedPorts));
        setSelectedPortToAdd(null);
        setTemplateMessage(null);
      } catch (err) {
        console.error(err);
        setTemplateError("Failed to load template details.");
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadTemplate();
  };

  const addPortToTemplate = () => {
    if (!selectedPortToAdd) return;
    const selectedPort = portsCatalog.find((port) => port.id === selectedPortToAdd);
    if (!selectedPort) return;

    if (templatePorts.some((port) => port.port_of_call_id === selectedPort.id)) {
      setTemplateError("This port is already in the template.");
      return;
    }

    const updatedPorts = normalizeTemplatePorts([
      ...templatePorts,
      {
        id: null,
        port_of_call_id: selectedPort.id,
        port_of_call: {
          id: selectedPort.id,
          name: selectedPort.name,
          country: {
            name: selectedPort.country_name,
            iso3_code: selectedPort.country_iso3 || "",
          },
        },
        port_order: templatePorts.length + 1,
        day_arrival: templatePorts.length + 1,
        day_departure: templatePorts.length + 1,
        route_to_next_mode: "",
        events: [],
      },
    ]);
    setTemplatePorts(reconcileTemplatePortEvents(updatedPorts));
    setSelectedPortToAdd(null);
  };

  const getPortOfCallFromCatalogPort = (port) => ({
    id: port.id,
    name: port.name,
    country: {
      name: port.country_name,
      iso3_code: port.country_iso3 || "",
    },
  });

  const changeTemplatePort = (index, portId) => {
    const selectedPort = portsCatalog.find((port) => String(port.id) === String(portId));
    if (!selectedPort) return;

    if (
      templatePorts.some(
        (port, idx) =>
          idx !== index && String(port.port_of_call_id) === String(selectedPort.id)
      )
    ) {
      setTemplateError("This port is already in the template.");
      return;
    }

    setTemplateError(null);
    setTemplatePorts((prevPorts) =>
      reconcileTemplatePortEvents(
        prevPorts.map((port, idx) => {
          if (idx !== index) return port;

          const locationCountryIso3 = selectedPort.country_iso3 || "";
          return {
            ...port,
            port_of_call_id: selectedPort.id,
            port_of_call: getPortOfCallFromCatalogPort(selectedPort),
            events: (port.events || []).map((event) =>
              event.location_mode === "port"
                ? {
                    ...event,
                    location_country_iso3: locationCountryIso3,
                    location_country_touched: false,
                  }
                : event
            ),
          };
        })
      )
    );
  };

  const removeTemplatePort = (index) => {
    const updatedPorts = templatePorts.filter((_, idx) => idx !== index);
    setTemplatePorts(reconcileTemplatePortEvents(updatedPorts));
  };

  const moveTemplatePort = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= templatePorts.length) return;

    const updatedPorts = templatePorts.slice();
    const [moved] = updatedPorts.splice(index, 1);
    updatedPorts.splice(targetIndex, 0, moved);
    setTemplatePorts(reconcileTemplatePortEvents(updatedPorts));
  };

  const updateTemplatePortField = (index, field, value) => {
    const updatedPorts = templatePorts.map((port, idx) =>
      idx === index ? { ...port, [field]: value } : port
    );
    setTemplatePorts(updatedPorts);
  };

  const getTemplateEventDefaultDay = (port, placement = "port") => {
    if (placement === "segment") {
      return port?.day_departure || port?.day_arrival || "";
    }
    return port?.day_arrival || port?.day_departure || "";
  };

  const addEventToTemplatePort = (portIndex, placement = "port") => {
    setTemplatePorts((prevPorts) => {
      const nextPort = prevPorts[portIndex + 1] || null;
      const updatedPorts = prevPorts.map((port, idx) => {
        if (idx !== portIndex) return port;
        const defaultCountry = port.port_of_call?.country?.iso3_code || "";
        return {
          ...port,
          events: [
            ...(port.events || []),
            normalizeTemplateEvent(
              {
                placement,
                day_number: getTemplateEventDefaultDay(port, placement),
                segment_end_port_of_call_id:
                  placement === "segment" ? nextPort?.port_of_call_id || null : null,
                location_country_iso3: defaultCountry,
              },
              { location_country_iso3: defaultCountry }
            ),
          ],
        };
      });
      return reconcileTemplatePortEvents(updatedPorts);
    });
  };

  const removeEventFromTemplatePort = (portIndex, eventIndex) => {
    if (activeTemplateEventMapKey === templateEventKey(portIndex, eventIndex)) {
      mapCtx?.tempPoint?.remove?.();
      setActiveTemplateEventMapKey(null);
    }
    setTemplatePorts((prevPorts) =>
      reconcileTemplatePortEvents(
        prevPorts.map((port, idx) =>
          idx === portIndex
            ? { ...port, events: (port.events || []).filter((_, innerIdx) => innerIdx !== eventIndex) }
            : port
        )
      )
    );
  };

  const updateTemplateEventField = (portIndex, eventIndex, field, value) => {
    setTemplatePorts((prevPorts) =>
      reconcileTemplatePortEvents(
        prevPorts.map((port, idx) => {
          if (idx !== portIndex) return port;
          const updatedEvents = (port.events || []).map((event, innerIdx) => {
            if (innerIdx !== eventIndex) return event;
            const nextEvent = { ...event, [field]: value };
            if (field === "placement" && value !== "segment") {
              nextEvent.segment_end_port_of_call_id = null;
              nextEvent.segment_route_behavior = "waypoint";
            }
            if (field === "location_mode") {
              if (value === "port") {
                nextEvent.custom_location = null;
                nextEvent.custom_location_label = "";
                nextEvent.nearby_port_of_call_id = null;
                nextEvent.location_country_iso3 = port.port_of_call?.country?.iso3_code || "";
                nextEvent.location_country_touched = false;
              }
              if (value === "nearby_port") {
                nextEvent.custom_location = null;
              }
              if (value === "custom") {
                nextEvent.nearby_port_of_call_id = null;
              }
            }
            if (field === "location_country_iso3") {
              nextEvent.location_country_touched = true;
            }
            return nextEvent;
          });
          return { ...port, events: updatedEvents };
        })
      )
    );
  };

  const updateTemplateEventNearbyPort = (portIndex, eventIndex, nearbyPortId) => {
    const nearbyPort = portsCatalog.find((port) => String(port.id) === String(nearbyPortId));
    if (!nearbyPort?.location?.coordinates) return;
    setTemplatePorts((prevPorts) =>
      reconcileTemplatePortEvents(
        prevPorts.map((port, idx) => {
          if (idx !== portIndex) return port;
          return {
            ...port,
            events: (port.events || []).map((event, innerIdx) =>
              innerIdx === eventIndex
                ? {
                    ...event,
                    location_mode: "nearby_port",
                    nearby_port_of_call_id: nearbyPort.id,
                    custom_location: nearbyPort.location,
                    custom_location_label: nearbyPort.name,
                    location_country_iso3: nearbyPort.country_iso3 || "",
                    location_country_touched: false,
                  }
                : event
            ),
          };
        })
      )
    );
  };

  const setTemplateEventCustomLocation = (portIndex, eventIndex, lon, lat) => {
    const lonNumber = Number(lon);
    const latNumber = Number(lat);
    if (!isValidLonLat(lonNumber, latNumber)) return;

    setTemplatePorts((prevPorts) =>
      reconcileTemplatePortEvents(
        prevPorts.map((port, idx) => {
          if (idx !== portIndex) return port;
          return {
            ...port,
            events: (port.events || []).map((event, innerIdx) =>
              innerIdx === eventIndex
                ? {
                    ...event,
                    location_mode: "custom",
                    nearby_port_of_call_id: null,
                    custom_location: geoJsonPoint(lonNumber, latNumber),
                  }
                : event
            ),
          };
        })
      )
    );
  };

  const lookupTemplateEventCountry = useCallback(async (portIndex, eventIndex, lon, lat) => {
    const lonNumber = Number(lon);
    const latNumber = Number(lat);
    if (!isValidLonLat(lonNumber, latNumber)) return;

    const lookupKey = templateEventKey(portIndex, eventIndex);
    setTemplateEventCountryLookupKey(lookupKey);
    try {
      const response = await axios({
        url: buildApiUrl("coordinate_lookup/"),
        method: "post",
        data: {
          latitude: latNumber,
          longitude: lonNumber,
        },
        headers: await getApiHeaders(),
      });

      const countryCode = response.data?.country_code || "";
      if (!countryCode) return;

      setTemplatePorts((prevPorts) =>
        reconcileTemplatePortEvents(
          prevPorts.map((port, idx) => {
            if (idx !== portIndex) return port;
            return {
              ...port,
              events: (port.events || []).map((event, innerIdx) =>
                innerIdx === eventIndex && !event.location_country_touched
                  ? {
                      ...event,
                      location_country_iso3: countryCode,
                    }
                  : event
              ),
            };
          })
        )
      );
    } catch (err) {
      console.error("Error looking up template event country:", err);
    } finally {
      setTemplateEventCountryLookupKey((currentKey) => (currentKey === lookupKey ? null : currentKey));
    }
  }, [getApiHeaders]);

  const startTemplateEventMapLocation = useCallback((portIndex, eventIndex) => {
    const tempPoint = mapCtx?.tempPoint;
    if (!tempPoint) {
      setTemplateError("Map coordinate picker is not available in this view.");
      return;
    }

    const event = templatePorts[portIndex]?.events?.[eventIndex];
    const coords = event?.custom_location?.coordinates || [];
    tempPoint.remove?.();

    if (isValidLonLat(Number(coords[0]), Number(coords[1]))) {
      tempPoint.createAt?.([Number(coords[0]), Number(coords[1])]);
    } else {
      tempPoint.createAtCenter?.();
    }

    setActiveTemplateEventMapKey(templateEventKey(portIndex, eventIndex));
    tempPoint.enableDrag?.(([lon, lat]) => {
      setTemplateEventCustomLocation(portIndex, eventIndex, Number(lon).toFixed(6), Number(lat).toFixed(6));
      lookupTemplateEventCountry(portIndex, eventIndex, lon, lat);
    });

    const currentCoords = tempPoint.getLonLat?.();
    if (currentCoords) {
      const [lon, lat] = currentCoords;
      setTemplateEventCustomLocation(portIndex, eventIndex, Number(lon).toFixed(6), Number(lat).toFixed(6));
      lookupTemplateEventCountry(portIndex, eventIndex, lon, lat);
    }
  }, [lookupTemplateEventCountry, mapCtx?.tempPoint, templatePorts]);

  const updateTemplateEventCoordinate = (portIndex, eventIndex, axis, rawValue) => {
    const event = templatePorts[portIndex]?.events?.[eventIndex];
    const currentLon = event?.custom_location?.coordinates?.[0] ?? "";
    const currentLat = event?.custom_location?.coordinates?.[1] ?? "";
    const nextLon = axis === "lon" ? rawValue : currentLon;
    const nextLat = axis === "lat" ? rawValue : currentLat;

    if (nextLon === "" || nextLat === "") {
      updateTemplateEventField(portIndex, eventIndex, "custom_location", null);
      return;
    }

    setTemplateEventCustomLocation(portIndex, eventIndex, nextLon, nextLat);
  };

  const availablePortOptions = portsCatalog
    .filter(
      (port) => !templatePorts.some((templatePort) => templatePort.port_of_call_id === port.id)
    )
    .map((port) => ({
      key: port.id,
      value: port.id,
      text: `${port.name} (${port.country_name || "Unknown"})`,
    }));
  const getTemplatePortOptions = (currentIndex) => {
    const usedPortIds = new Set(
      templatePorts
        .filter((_, index) => index !== currentIndex)
        .map((port) => String(port.port_of_call_id))
    );
    const options = portsCatalog
      .filter((port) => !usedPortIds.has(String(port.id)))
      .map((port) => ({
        key: port.id,
        value: port.id,
        text: `${port.name} (${port.country_name || "Unknown"})`,
      }));

    const currentPort = templatePorts[currentIndex];
    if (
      currentPort?.port_of_call_id &&
      !options.some((option) => String(option.value) === String(currentPort.port_of_call_id))
    ) {
      options.unshift({
        key: currentPort.port_of_call_id,
        value: currentPort.port_of_call_id,
        text: `${currentPort.port_of_call?.name || "Selected port"} (${
          currentPort.port_of_call?.country?.name || "Unknown"
        })`,
      });
    }

    return options;
  };

  const saveTemplate = async () => {
    if (!templateForm?.name?.trim() || !templateForm?.code?.trim()) {
      setTemplateError("Name and code are required.");
      return;
    }
    if (!templateForm?.tour_type_id) {
      setTemplateError("Tour type is required.");
      return;
    }

    setTemplateError(null);
    setTemplateMessage(null);
    setIsSavingTemplate(true);
    const payload = {
      name: templateForm.name.trim(),
      code: templateForm.code.trim(),
      duration: templateForm.duration === "" ? null : Number(templateForm.duration),
      description: templateForm.description || "",
      tour_type_id: templateForm.tour_type_id,
      port_orders: templatePorts.map((port, index) => ({
        id: port.id || undefined,
        port_of_call_id: port.port_of_call_id,
        port_order: index + 1,
        day_arrival: port.day_arrival === "" ? null : Number(port.day_arrival),
        day_departure: port.day_departure === "" ? null : Number(port.day_departure),
        route_to_next_mode:
          index === templatePorts.length - 1
            ? null
            : normalizeTemplateSegmentRouteMode(port.route_to_next_mode) || null,
        events: (port.events || []).map((event) => ({
          id: event.id || undefined,
          name: event.name?.trim() || "",
          description: event.description || "",
          day_number: event.day_number === "" ? null : Number(event.day_number),
          duration: event.duration || null,
          placement: event.placement || "port",
          segment_end_port_of_call_id:
            event.placement === "segment" ? event.segment_end_port_of_call_id || null : null,
          route_mode: event.route_mode || null,
          segment_route_behavior:
            event.placement === "segment"
              ? event.segment_route_behavior || "waypoint"
              : "waypoint",
          display_as_port_card: Boolean(event.display_as_port_card),
          custom_location:
            event.location_mode === "custom" || event.location_mode === "nearby_port"
              ? event.custom_location || null
              : null,
          custom_location_label:
            event.location_mode === "port" ? "" : event.custom_location_label || "",
          location_country_iso3:
            event.location_country_iso3 ||
            port.port_of_call?.country?.iso3_code ||
            "",
        })),
      })),
    };

    try {
      if (templateForm.id) {
        await axios({
          url: buildApiUrl(`tour_template/${templateForm.id}/`),
          method: "put",
          data: payload,
          headers: await getApiHeaders(),
        });
        setTemplateMessage("Template updated.");
      } else {
        await axios({
          url: buildApiUrl("tour_template/"),
          method: "post",
          data: payload,
          headers: await getApiHeaders(),
        });
        setTemplateMessage("Template created.");
      }
      await fetchTemplates();
      await fetchTemplateOptions();
      resetTemplateForm(true);
    } catch (err) {
      console.error(err);
      setTemplateError(err.response?.data?.error || "Failed to save template.");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const openOrCreateTemplateMap = async () => {
    if (!templateForm?.id) return;
    if (!setActiveMapToEdit || !fetchFullMapData) {
      setTemplateError("Map editor is not available in this view.");
      return;
    }

    setTemplateError(null);
    setTemplateMessage(null);
    setIsLoadingTemplateMap(true);
    try {
      let ensuredMapId = templateForm.tour_map?.id || null;
      let nextTemplateMap = templateForm.tour_map || null;

      if (!ensuredMapId) {
        const response = await axios({
          url: buildApiUrl(`tour_template/${templateForm.id}/ensure-tour-map/`),
          method: "post",
          headers: await getApiHeaders(),
        });
        ensuredMapId = response?.data?.map_root_id;
        nextTemplateMap = response?.data?.template?.tour_map || null;
        if (!ensuredMapId) {
          throw new Error("Template map was not created.");
        }
        setTemplateForm((prev) =>
          prev
            ? {
                ...prev,
                tour_map: nextTemplateMap,
              }
            : prev
        );
        await fetchTemplates();
        await fetchTemplateOptions();
      }

      const fullMapData = await fetchFullMapData(ensuredMapId);
      if (!fullMapData) {
        throw new Error("Template map was created but could not be loaded.");
      }

      setActiveMapToEdit(fullMapData);
      setShowTourListBar(false);
    } catch (err) {
      console.error(err);
      setTemplateError(
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to open the dedicated template map."
      );
    } finally {
      setIsLoadingTemplateMap(false);
    }
  };

  const deleteTemplate = async (template) => {
    const confirmDelete = window.confirm(`Delete template "${template.name}"?`);
    if (!confirmDelete) return;
    try {
      await axios({
        url: buildApiUrl(`tour_template/${template.id}/`),
        method: "delete",
        headers: await getApiHeaders(),
      });
      await fetchTemplates();
      await fetchTemplateOptions();
      if (templateForm?.id === template.id) {
        resetTemplateForm();
      }
    } catch (err) {
      console.error(err);
      setTemplateError("Failed to delete template.");
    }
  };

  const templateCount = templates.length;
  const tourTemplateOptions = useMemo(() => {
    if (templates.length > 0) {
      return templates.map((template) => {
        const portCount = template.port_orders_count ?? template.port_orders?.length ?? 0;
        const descriptionParts = [
          template.duration ? `${template.duration} days` : null,
          portCount ? `${portCount} ports` : null,
          template.tour_type?.name || null,
        ].filter(Boolean);
        return {
          key: template.id,
          value: template.id,
          text: template.name,
          description: descriptionParts.join(" | "),
        };
      });
    }
    return listOfTourTempl;
  }, [listOfTourTempl, templates]);
  const selectedNewTourTemplate = useMemo(() => {
    if (!pickedTemplate) return null;
    return (
      templates.find((template) => String(template.id) === String(pickedTemplate)) ||
      listOfTourTempl.find((template) => String(template.value) === String(pickedTemplate)) ||
      null
    );
  }, [listOfTourTempl, pickedTemplate, templates]);
  const selectedNewTourDuration = Number(selectedNewTourTemplate?.duration || 0);
  const selectedNewTourPortCount =
    selectedNewTourTemplate?.port_orders_count ??
    selectedNewTourTemplate?.port_orders?.length ??
    null;
  const newTourEndDate = useMemo(() => {
    if (!currentDate || !selectedNewTourDuration) return null;
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + Math.max(selectedNewTourDuration, 1) - 1);
    return endDate;
  }, [currentDate, selectedNewTourDuration]);
  const formatTourDate = (date) =>
    date
      ? date.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "Not set";
  const selectedTourTypeName = tourTypes.find(
    (type) => type.id === templateForm?.tour_type_id
  )?.name;
  const countryOptions = useMemo(
    () =>
      countries
        .filter((country) => country?.iso3_code)
        .map((country) => ({
          key: country.iso3_code,
          value: country.iso3_code,
          text: country.name || country.iso3_code,
          description: country.iso3_code,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [countries]
  );
  const templateSurfaceStyle = {
    background: "linear-gradient(180deg, #f8fbff 0%, #eef3f8 100%)",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: "28px",
    boxShadow: "0 22px 44px rgba(15, 23, 42, 0.12)",
    padding: isMobile ? "16px" : "20px",
    maxHeight: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 120px)",
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
  };
  const templateHeroStyle = {
    background: "linear-gradient(135deg, #16324a 0%, #2f6ea3 100%)",
    borderRadius: "22px",
    color: "#ffffff",
    padding: isMobile ? "18px" : "22px",
    marginBottom: "18px",
    boxShadow: "0 18px 40px rgba(22, 50, 74, 0.32)",
  };
  const templateEyebrowStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.14)",
    color: "#dbeafe",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "12px",
  };
  const templateHeroStatStyle = {
    borderRadius: "14px",
    padding: "8px 12px",
    background: "rgba(255, 255, 255, 0.16)",
    color: "#f8fafc",
    fontWeight: 600,
    margin: 0,
  };
  const templateToolbarStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "16px",
  };
  const templateSecondaryButtonStyle = {
    borderRadius: "14px",
    background: "#ffffff",
    border: "1px solid #d7e0eb",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  };
  const templatePrimaryButtonStyle = {
    borderRadius: "14px",
    background: "#1d6fd8",
    color: "#ffffff",
    boxShadow: "0 12px 24px rgba(29, 111, 216, 0.26)",
  };
  const templateSectionStyle = {
    background: "rgba(255, 255, 255, 0.96)",
    border: "1px solid #dbe4ee",
    borderRadius: "22px",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
    padding: "18px",
  };
  const templateSectionTitleStyle = {
    margin: 0,
    color: "#0f172a",
  };
  const templateSectionCopyStyle = {
    margin: "6px 0 0",
    color: "#5b6472",
    fontSize: "0.95rem",
    lineHeight: 1.5,
  };
  const templateListStyle = {
    display: "grid",
    gap: "14px",
    paddingRight: "4px",
  };
  const templateCardStyle = {
    width: "100%",
    margin: 0,
    borderRadius: "20px",
    border: "1px solid #dbe4ee",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
    background: "linear-gradient(180deg, #ffffff 0%, #f9fbfd 100%)",
  };
  const templateEmptyStateStyle = {
    ...templateSectionStyle,
    textAlign: "center",
    padding: "28px 18px",
  };
  const fieldLabelStyle = {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "#334155",
    marginBottom: "6px",
  };
  const textInputStyle = {
    width: "100%",
    borderRadius: "14px",
    border: "1px solid #d7e0eb",
    padding: "12px 14px",
    background: "#ffffff",
    color: "#0f172a",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.03)",
  };
  const selectInputStyle = {
    ...textInputStyle,
    padding: 0,
  };
  const textAreaStyle = {
    ...textInputStyle,
    minHeight: "110px",
    resize: "vertical",
  };
  const templatePortCardStyle = {
    borderRadius: "18px",
    border: "1px solid #dbe4ee",
    background: "linear-gradient(180deg, #ffffff 0%, #f7fafc 100%)",
    padding: "16px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  };
  const templatePortIndexStyle = {
    width: "36px",
    height: "36px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    color: "#1e3a8a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    flexShrink: 0,
  };
  const templatePortActionStyle = {
    borderRadius: "12px",
    background: "#ffffff",
    border: "1px solid #d7e0eb",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
  };
  const templateEventCardStyle = {
    borderRadius: "16px",
    border: "1px solid #dbe4ee",
    background: "#ffffff",
    padding: "14px",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
  };
  const controlPanelStyle = {
    background: "#f8fafc",
    border: "1px solid #dbe3ee",
    borderRadius: "18px",
    padding: isMobile ? "10px" : "12px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  };
  const controlRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
  };
  const segmentedGroupStyle = {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
  };
  const segmentedButtonStyle = {
    minHeight: isMobile ? "38px" : "36px",
    fontWeight: 700,
    boxShadow: "none",
  };
  const toolbarActionStyle = {
    borderRadius: "12px",
    minHeight: isMobile ? "38px" : "36px",
    fontWeight: 700,
    boxShadow: "none",
  };
  const listCountPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#e2e8f0",
    color: "#334155",
    fontSize: "0.82rem",
    fontWeight: 700,
  };
  const tourCardStyle = {
    width: "100%",
    margin: "0 0 14px",
    borderRadius: "18px",
    border: "1px solid #dbe4ee",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.07)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  };
  const cardOpenButtonStyle = {
    borderRadius: "12px",
    background: "#f1f5f9",
    border: "1px solid #d7e0eb",
    boxShadow: "none",
  };
  const newTourModalHeroStyle = {
    background: "linear-gradient(135deg, #14344f 0%, #236c8f 58%, #2f8f83 100%)",
    color: "#ffffff",
    padding: isMobile ? "18px" : "22px",
  };
  const newTourModalTitleStyle = {
    color: "#ffffff",
    margin: 0,
    lineHeight: 1.2,
  };
  const newTourModalCopyStyle = {
    color: "rgba(255, 255, 255, 0.82)",
    lineHeight: 1.5,
    margin: "8px 0 0",
  };
  const newTourBodyStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.2fr) minmax(240px, 0.8fr)",
    gap: "16px",
    background: "#f8fafc",
  };
  const newTourPanelStyle = {
    border: "1px solid #dbe4ee",
    borderRadius: "18px",
    background: "#ffffff",
    padding: "16px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  };
  const newTourSummaryRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    borderBottom: "1px solid #edf2f7",
    padding: "10px 0",
  };
  const newTourSummaryLabelStyle = {
    color: "#64748b",
    fontSize: "0.84rem",
    fontWeight: 700,
  };
  const newTourSummaryValueStyle = {
    color: "#0f172a",
    fontWeight: 800,
    textAlign: "right",
  };
  const newTourActionButtonStyle = {
    borderRadius: "12px",
    fontWeight: 700,
  };
  const tourListGridStyle = {
    margin: "10px",
    ...(isMobile ? {
      flex: "1 1 auto",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
      overflow: "hidden",
    } : {}),
  };
  const tourCardsRowStyle = isMobile ? {
    flex: "1 1 0",
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
    paddingBottom: 0,
  } : undefined;
  const tourCardsStyle = {
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    paddingBottom: isMobile ? "16px" : "60px",
    paddingTop: "6px",
    gap: "0",
    ...(isMobile ? {
      flex: "1 1 auto",
      height: "100%",
      minHeight: 0,
      maxHeight: "none",
      overscrollBehavior: "contain",
      touchAction: "pan-y",
      WebkitOverflowScrolling: "touch",
    } : {
      maxHeight: "80vh",
    }),
  };

  return (
    <SidebarWrapper
      isMobile={isMobile}
      sidebarConfig={sidebarConfig}
      onClose={closeTourListBar}
      onHeightChange={onHeightChange}
      onToggleVisibility={(nextVisible) => {
        if (nextVisible === false) closeTourListBar();
      }}
      panelLabel="Tours"
      zIndex={505}
    >
      <Grid style={tourListGridStyle}>
        <Grid.Row centered>
          <Grid.Column width={16}>
            <div style={controlPanelStyle}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={controlRowStyle}>
                  <Button.Group size="small" style={segmentedGroupStyle}>
                    <Button
                      active={activeView === "tours"}
                      onClick={() => setActiveView("tours")}
                      style={segmentedButtonStyle}
                    >
                      Tours
                    </Button>
                    <Button
                      active={activeView === "templates"}
                      onClick={() => setActiveView("templates")}
                      style={segmentedButtonStyle}
                    >
                      Templates
                    </Button>
                  </Button.Group>

                  {activeView === "tours" ? (
                    <Button
                      icon="plus"
                      content={isMobile ? undefined : "New Tour"}
                      onClick={openNewTourModal}
                      disabled={!activeUser?.object_id || isUserSwitching}
                      primary
                      style={toolbarActionStyle}
                    />
                  ) : null}
                </div>

                {activeView === "tours" ? (
                  <div style={controlRowStyle}>
                    <Button.Group size="small" style={segmentedGroupStyle}>
                      <Button
                        active={activeTab === "current"}
                        onClick={() => setActiveTab("current")}
                        style={segmentedButtonStyle}
                      >
                        Current Year
                      </Button>
                      <Button
                        active={activeTab === "historic"}
                        onClick={() => setActiveTab("historic")}
                        style={segmentedButtonStyle}
                      >
                        Historic
                      </Button>
                    </Button.Group>

                    <div style={listCountPillStyle}>
                      {cards.length} {cards.length === 1 ? "tour" : "tours"}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>

        {activeView === "tours" ? (
          <>
            {isUserSwitching && (
              <Grid.Row centered>
                <Grid.Column width={16}>
                  <Message info icon>
                    <Icon name="sync" loading />
                    <Message.Content>
                      <Message.Header>Switching active user</Message.Header>
                      Tour data is still loading. Please wait a moment before opening a tour.
                    </Message.Content>
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}

            <Modal
              onClose={closeNewTourModal}
              open={open}
              size="small"
              closeOnDimmerClick={!isCreatingTour}
            >
              <Modal.Header style={newTourModalHeroStyle}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <Icon name="ship" style={{ marginTop: "3px" }} />
                  <div>
                    <Header as="h3" style={newTourModalTitleStyle}>
                      Create Tour From Template
                    </Header>
                    <p style={newTourModalCopyStyle}>
                      Pick the sailing blueprint and embarkation date. The tour opens immediately
                      after creation so the operations setup can continue.
                    </p>
                  </div>
                </div>
              </Modal.Header>
              <Modal.Content style={newTourBodyStyle}>
                <div style={newTourPanelStyle}>
                  <Form>
                    {newTourError ? (
                      <Message
                        negative
                        icon="warning circle"
                        content={newTourError}
                        style={{ borderRadius: "14px" }}
                      />
                    ) : null}

                    <Form.Field required>
                      <label style={fieldLabelStyle}>Itinerary template</label>
                      <Select
                        fluid
                        search
                        selection
                        loading={templatesLoading && tourTemplateOptions.length === 0}
                        placeholder="Search by route or template name"
                        value={pickedTemplate}
                        options={tourTemplateOptions}
                        noResultsMessage="No matching templates"
                        onChange={(_e, data) => {
                          setNewTourError(null);
                          setPickedTemplate(data.value);
                        }}
                        style={{
                          ...textInputStyle,
                          padding: 0,
                        }}
                      />
                    </Form.Field>

                    <Form.Field required>
                      <label style={fieldLabelStyle}>Embarkation date</label>
                      <div
                        style={{
                          ...textInputStyle,
                          padding: "0 12px",
                          display: "flex",
                          alignItems: "center",
                          minHeight: "44px",
                        }}
                      >
                        <Icon name="calendar alternate outline" style={{ color: "#64748b" }} />
                        <DatePicker
                          selected={currentDate}
                          onChange={onDateChange}
                          dateFormat="yyyy-MM-dd"
                          placeholderText="Select embarkation date"
                          className="ui input"
                        />
                      </div>
                    </Form.Field>

                    <Message
                      icon="info circle"
                      content="Creation uses noon local time for the start date, matching the existing tour schedule behavior."
                      style={{ borderRadius: "14px", marginBottom: 0 }}
                    />
                  </Form>
                </div>

                <div style={newTourPanelStyle}>
                  <Header as="h4" style={{ marginTop: 0, color: "#0f172a" }}>
                    Sailing Preview
                  </Header>

                  <div style={newTourSummaryRowStyle}>
                    <span style={newTourSummaryLabelStyle}>Template</span>
                    <span style={newTourSummaryValueStyle}>
                      {selectedNewTourTemplate?.name || selectedNewTourTemplate?.text || "Not selected"}
                    </span>
                  </div>
                  <div style={newTourSummaryRowStyle}>
                    <span style={newTourSummaryLabelStyle}>Start</span>
                    <span style={newTourSummaryValueStyle}>{formatTourDate(currentDate)}</span>
                  </div>
                  <div style={newTourSummaryRowStyle}>
                    <span style={newTourSummaryLabelStyle}>End</span>
                    <span style={newTourSummaryValueStyle}>
                      {newTourEndDate ? formatTourDate(newTourEndDate) : "Needs duration"}
                    </span>
                  </div>
                  <div style={newTourSummaryRowStyle}>
                    <span style={newTourSummaryLabelStyle}>Duration</span>
                    <span style={newTourSummaryValueStyle}>
                      {selectedNewTourDuration ? `${selectedNewTourDuration} days` : "Not set"}
                    </span>
                  </div>
                  <div style={{ ...newTourSummaryRowStyle, borderBottom: 0 }}>
                    <span style={newTourSummaryLabelStyle}>Ports</span>
                    <span style={newTourSummaryValueStyle}>
                      {selectedNewTourPortCount != null ? selectedNewTourPortCount : "Not set"}
                    </span>
                  </div>

                  <Button
                    type="button"
                    fluid
                    basic
                    icon="map signs"
                    content="Review templates"
                    onClick={() => {
                      closeNewTourModal();
                      setActiveView("templates");
                    }}
                    disabled={isCreatingTour}
                    style={{ ...newTourActionButtonStyle, marginTop: "14px" }}
                  />
                </div>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  onClick={closeNewTourModal}
                  disabled={isCreatingTour}
                  style={newTourActionButtonStyle}
                >
                  Cancel
                </Button>
                <Button
                  content="Create and open"
                  labelPosition="right"
                  icon="checkmark"
                  onClick={addNewTour}
                  positive
                  loading={isCreatingTour}
                  disabled={!pickedTemplate || !currentDate || isCreatingTour}
                  style={newTourActionButtonStyle}
                />
              </Modal.Actions>
            </Modal>

            {userTours ? (
              <Grid.Row centered style={tourCardsRowStyle}>
                <Card.Group
                  centered
                  style={tourCardsStyle}
                >
                  {cards.map((card) => (
                    <Card key={card.key} fluid style={tourCardStyle}>
                      <Card.Content textAlign="left">
                        <Button
                          floated="right"
                          key={card.key}
                          onClick={() => openTour(card.key)}
                          icon="folder open outline"
                          disabled={isUserSwitching}
                          style={cardOpenButtonStyle}
                        />
                        <Card.Header>{card.header}</Card.Header>
                        <Card.Meta style={{ marginTop: "8px", color: "#64748b" }}>{card.meta}</Card.Meta>
                        <Card.Description>{card.description}</Card.Description>
                      </Card.Content>
                    </Card>
                  ))}
                </Card.Group>
              </Grid.Row>
            ) : null}
          </>
        ) : (
          <>
            <Grid.Row centered>
              <Grid.Column width={16}>
                <Dimmer.Dimmable
                  blurring
                  dimmed={isSavingTemplate}
                  style={{ position: "relative" }}
                >
                  <Dimmer active={isSavingTemplate} inverted>
                    <Loader size="large">Saving itinerary template...</Loader>
                  </Dimmer>

                  <div style={templateSurfaceStyle}>
                    <div style={templateHeroStyle}>
                      <div style={templateEyebrowStyle}>
                        <Icon name="map signs" style={{ margin: 0 }} />
                        Template Studio
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: "14px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ maxWidth: "420px" }}>
                          <Header as="h3" style={{ color: "#ffffff", margin: 0 }}>
                            Itinerary Templates
                          </Header>
                          <p
                            style={{
                              margin: "8px 0 0",
                              color: "rgba(255, 255, 255, 0.82)",
                              lineHeight: 1.6,
                            }}
                          >
                            {showTemplateEditor
                              ? "Shape the route, pacing, and reusable structure before creating a new itinerary."
                              : "Manage reusable itinerary blueprints and keep template data tidy before tours are created."}
                          </p>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            justifyContent: isMobile ? "flex-start" : "flex-end",
                          }}
                        >
                          <div style={templateHeroStatStyle}>{templateCount} templates</div>
                          <div style={templateHeroStatStyle}>
                            {showTemplateEditor ? `${templatePorts.length} ports` : "Reusable routes"}
                          </div>
                          {showTemplateEditor && selectedTourTypeName ? (
                            <div style={templateHeroStatStyle}>{selectedTourTypeName}</div>
                          ) : null}
                        </div>
                      </div>

                      <div style={templateToolbarStyle}>
                        {showTemplateEditor ? (
                          <Button
                            type="button"
                            icon="arrow left"
                            content="Back to list"
                            onClick={() => resetTemplateForm()}
                            disabled={isSavingTemplate}
                            style={templateSecondaryButtonStyle}
                          />
                        ) : (
                          <Button
                            type="button"
                            icon="plus"
                            content="New Template"
                            onClick={startNewTemplate}
                            style={templatePrimaryButtonStyle}
                          />
                        )}

                        <Button
                          type="button"
                          icon="refresh"
                          content="Refresh"
                          onClick={fetchTemplates}
                          loading={templatesLoading}
                          disabled={isSavingTemplate}
                          style={{
                            ...templateSecondaryButtonStyle,
                            background: showTemplateEditor ? "#ffffff" : "rgba(255, 255, 255, 0.94)",
                          }}
                        />
                      </div>
                    </div>

                    {templateMessage && (
                      <Message
                        positive
                        icon="check circle"
                        content={templateMessage}
                        style={{ borderRadius: "16px", marginBottom: "16px" }}
                      />
                    )}

                    {templateError && (
                      <Message
                        negative
                        icon="warning circle"
                        content={templateError}
                        style={{ borderRadius: "16px", marginBottom: "16px" }}
                      />
                    )}

                    {!showTemplateEditor ? (
                      <div style={templateListStyle}>
                        {templatesLoading && templateCount === 0 ? (
                          <div style={templateEmptyStateStyle}>
                            <Loader active inline="centered" size="small" />
                            <p style={{ margin: "14px 0 0", color: "#5b6472" }}>
                              Loading templates...
                            </p>
                          </div>
                        ) : null}

                        {!templatesLoading && templateCount === 0 ? (
                          <div style={templateEmptyStateStyle}>
                            <Icon
                              name="map outline"
                              size="big"
                              style={{ color: "#1d6fd8", marginBottom: "12px" }}
                            />
                            <Header as="h4" style={{ margin: 0 }}>
                              No templates yet
                            </Header>
                            <p style={{ margin: "8px 0 0", color: "#5b6472" }}>
                              Start with a draft template so new tours can be created faster.
                            </p>
                            <Button
                              type="button"
                              icon="plus"
                              content="Create first template"
                              onClick={startNewTemplate}
                              style={{ ...templatePrimaryButtonStyle, marginTop: "16px" }}
                            />
                          </div>
                        ) : null}

                        {(templates || []).map((template) => (
                          <Card key={template.id} fluid style={templateCardStyle}>
                            <Card.Content>
                              <Button
                                type="button"
                                floated="right"
                                icon="edit"
                                onClick={() => selectTemplateForEdit(template)}
                                style={templatePortActionStyle}
                              />
                              <Card.Header style={{ color: "#0f172a", marginBottom: "8px" }}>
                                {template.name}
                              </Card.Header>
                              <Card.Meta style={{ color: "#64748b" }}>
                                {template.duration ? `${template.duration} days` : "Duration not set"}
                              </Card.Meta>
                              <Card.Description style={{ marginTop: "14px" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                  }}
                                >
                                  <Label
                                    style={{
                                      borderRadius: "999px",
                                      background: "#e0f2fe",
                                      color: "#075985",
                                    }}
                                  >
                                    {(template.port_orders_count ?? template.port_orders?.length ?? 0)} ports
                                  </Label>
                                  {template.tour_type?.name ? (
                                    <Label
                                      style={{
                                        borderRadius: "999px",
                                        background: "#eef2ff",
                                        color: "#3730a3",
                                      }}
                                    >
                                      {template.tour_type.name}
                                    </Label>
                                  ) : null}
                                </div>
                              </Card.Description>
                            </Card.Content>
                            <Card.Content extra>
                              <Button
                                type="button"
                                basic
                                color="red"
                                size="tiny"
                                icon="trash"
                                content="Delete"
                                onClick={() => deleteTemplate(template)}
                              />
                            </Card.Content>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gap: "14px",
                          paddingRight: "4px",
                        }}
                      >
                        <Form style={{ display: "grid", gap: "14px" }}>
                          <div style={templateSectionStyle}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Header as="h4" style={templateSectionTitleStyle}>
                                {templateForm.id ? "Edit Template" : "New Template"}
                              </Header>
                              <Label
                                style={{
                                  borderRadius: "999px",
                                  background: templateForm.id ? "#dbeafe" : "#dcfce7",
                                  color: templateForm.id ? "#1d4ed8" : "#166534",
                                }}
                              >
                                {templateForm.id ? "Existing template" : "Draft"}
                              </Label>
                            </div>
                            <p style={templateSectionCopyStyle}>
                              Define the template identity, duration, and tour type before building
                              the port sequence.
                            </p>

                            <div style={{ display: "grid", gap: "14px", marginTop: "16px" }}>
                              <Form.Field>
                                <label style={fieldLabelStyle}>Name</label>
                                <input
                                  value={templateForm.name}
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                                  }
                                  style={textInputStyle}
                                />
                              </Form.Field>

                              <Form.Field>
                                <label style={fieldLabelStyle}>Code</label>
                                <input
                                  value={templateForm.code}
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({ ...prev, code: e.target.value }))
                                  }
                                  style={textInputStyle}
                                />
                              </Form.Field>

                              <Form.Field>
                                <label style={fieldLabelStyle}>Duration (days)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={templateForm.duration}
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      duration: e.target.value,
                                    }))
                                  }
                                  style={textInputStyle}
                                />
                              </Form.Field>

                              <Form.Field>
                                <label style={fieldLabelStyle}>Description</label>
                                <textarea
                                  value={templateForm.description}
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      description: e.target.value,
                                    }))
                                  }
                                  style={textAreaStyle}
                                />
                              </Form.Field>

                              <Form.Field>
                                <label style={fieldLabelStyle}>Tour Type</label>
                                <Select
                                  fluid
                                  placeholder="Select tour type"
                                  value={templateForm.tour_type_id}
                                  options={tourTypes.map((type) => ({
                                    key: type.id,
                                    value: type.id,
                                    text: type.name,
                                  }))}
                                  onChange={(_e, data) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      tour_type_id: data.value,
                                    }))
                                  }
                                  style={{
                                    ...textInputStyle,
                                    padding: 0,
                                  }}
                                />
                              </Form.Field>
                            </div>
                          </div>

                          <div style={templateSectionStyle}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Header as="h4" style={templateSectionTitleStyle}>
                                Ports of Call
                              </Header>
                              <Label
                                style={{
                                  borderRadius: "999px",
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                }}
                              >
                                {templatePorts.length} selected
                              </Label>
                            </div>
                            <p style={templateSectionCopyStyle}>
                              Search the catalog, add ports in sequence, then fine-tune arrival
                              and departure days for each stop.
                            </p>

                            <div
                              style={{
                                display: "grid",
                                gap: "10px",
                                marginTop: "16px",
                              }}
                            >
                              <Form.Field style={{ margin: 0 }}>
                                <label style={fieldLabelStyle}>Available ports</label>
                                <Select
                                  fluid
                                  search
                                  placeholder="Search and add a port"
                                  value={selectedPortToAdd}
                                  options={availablePortOptions}
                                  onChange={(_e, data) => setSelectedPortToAdd(data.value)}
                                  style={{
                                    ...textInputStyle,
                                    padding: 0,
                                  }}
                                />
                              </Form.Field>

                              <Button
                                type="button"
                                content="Add Port"
                                icon="plus"
                                onClick={addPortToTemplate}
                                disabled={!selectedPortToAdd || isSavingTemplate}
                                style={templatePrimaryButtonStyle}
                              />
                            </div>

                            {templatePorts.length === 0 ? (
                              <div
                                style={{
                                  marginTop: "16px",
                                  borderRadius: "18px",
                                  border: "1px dashed #bfdbfe",
                                  padding: "22px 16px",
                                  textAlign: "center",
                                  background: "#f8fbff",
                                  color: "#4b5563",
                                }}
                              >
                                No ports added yet.
                              </div>
                            ) : (
                              <div style={{ display: "grid", gap: "12px", marginTop: "16px" }}>
                                {templatePorts.map((port, index) => (
                                  <div
                                    key={`${port.port_of_call_id}-${index}`}
                                    style={templatePortCardStyle}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          gap: "12px",
                                        }}
                                      >
                                        <div style={templatePortIndexStyle}>{index + 1}</div>
                                        <div>
                                          <Header as="h5" style={{ margin: 0, color: "#0f172a" }}>
                                            {port.port_of_call?.name}
                                          </Header>
                                          <p
                                            style={{
                                              margin: "4px 0 0",
                                              color: "#64748b",
                                            }}
                                          >
                                            {port.port_of_call?.country?.name ||
                                              "Unknown country"}
                                          </p>
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Button
                                          type="button"
                                          icon="arrow up"
                                          size="tiny"
                                          onClick={() => moveTemplatePort(index, -1)}
                                          disabled={index === 0 || isSavingTemplate}
                                          style={templatePortActionStyle}
                                        />
                                        <Button
                                          type="button"
                                          icon="arrow down"
                                          size="tiny"
                                          onClick={() => moveTemplatePort(index, 1)}
                                          disabled={
                                            index === templatePorts.length - 1 || isSavingTemplate
                                          }
                                          style={templatePortActionStyle}
                                        />
                                        <Button
                                          type="button"
                                          icon="trash"
                                          size="tiny"
                                          color="red"
                                          onClick={() => removeTemplatePort(index)}
                                          disabled={isSavingTemplate}
                                        />
                                      </div>
                                    </div>

                                    <Form.Field style={{ margin: "14px 0 0" }}>
                                      <label style={fieldLabelStyle}>Port</label>
                                      <Select
                                        fluid
                                        search
                                        value={port.port_of_call_id}
                                        options={getTemplatePortOptions(index)}
                                        onChange={(_e, data) => changeTemplatePort(index, data.value)}
                                        disabled={isSavingTemplate}
                                        style={selectInputStyle}
                                      />
                                    </Form.Field>

                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                        gap: "12px",
                                        marginTop: "14px",
                                      }}
                                    >
                                      <Form.Field style={{ margin: 0 }}>
                                        <label style={fieldLabelStyle}>Day Arrival</label>
                                        <input
                                          type="number"
                                          min="1"
                                          value={port.day_arrival}
                                          onChange={(e) =>
                                            updateTemplatePortField(
                                              index,
                                              "day_arrival",
                                              e.target.value
                                            )
                                          }
                                          style={textInputStyle}
                                        />
                                      </Form.Field>

                                      <Form.Field style={{ margin: 0 }}>
                                        <label style={fieldLabelStyle}>Day Departure</label>
                                        <input
                                          type="number"
                                          min="1"
                                          value={port.day_departure}
                                          onChange={(e) =>
                                            updateTemplatePortField(
                                              index,
                                              "day_departure",
                                              e.target.value
                                            )
                                          }
                                          style={textInputStyle}
                                        />
                                      </Form.Field>

                                      <Form.Field style={{ margin: 0 }}>
                                        <label style={fieldLabelStyle}>To next</label>
                                        <Select
                                          fluid
                                          compact
                                          options={TEMPLATE_SEGMENT_ROUTE_OPTIONS}
                                          value={
                                            index === templatePorts.length - 1
                                              ? ""
                                              : normalizeTemplateSegmentRouteMode(port.route_to_next_mode)
                                          }
                                          onChange={(_, data) =>
                                            updateTemplatePortField(
                                              index,
                                              "route_to_next_mode",
                                              data.value || ""
                                            )
                                          }
                                          disabled={index === templatePorts.length - 1 || isSavingTemplate}
                                          style={selectInputStyle}
                                        />
                                      </Form.Field>
                                    </div>

                                    <div
                                      style={{
                                        marginTop: "16px",
                                        paddingTop: "14px",
                                        borderTop: "1px solid #e2e8f0",
                                        display: "grid",
                                        gap: "12px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          gap: "10px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <div>
                                          <Header as="h5" style={{ margin: 0, color: "#0f172a" }}>
                                            Template events
                                          </Header>
                                          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.9rem" }}>
                                            Add events at this port or on the next leg.
                                          </p>
                                        </div>
                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                          <Button
                                            type="button"
                                            size="tiny"
                                            icon="plus"
                                            content="Port Event"
                                            onClick={() => addEventToTemplatePort(index, "port")}
                                            disabled={isSavingTemplate}
                                            style={templatePortActionStyle}
                                          />
                                          <Button
                                            type="button"
                                            size="tiny"
                                            icon="plus"
                                            content="Leg Event"
                                            onClick={() => addEventToTemplatePort(index, "segment")}
                                            disabled={isSavingTemplate || index === templatePorts.length - 1}
                                            style={templatePortActionStyle}
                                          />
                                        </div>
                                      </div>

                                      {(port.events || []).length === 0 ? (
                                        <div
                                          style={{
                                            borderRadius: "14px",
                                            border: "1px dashed #cbd5e1",
                                            background: "#f8fafc",
                                            color: "#64748b",
                                            padding: "14px",
                                            textAlign: "center",
                                          }}
                                        >
                                          No events on this stop yet.
                                        </div>
                                      ) : (
                                        <div style={{ display: "grid", gap: "10px" }}>
                                          {(port.events || []).map((event, eventIndex) => {
                                            const nextPort = templatePorts[index + 1] || null;
                                            const nearbyPortOptions = portsCatalog
                                              .filter((nearbyPort) => nearbyPort.location?.coordinates?.length === 2)
                                              .map((nearbyPort) => ({
                                                key: nearbyPort.id,
                                                value: nearbyPort.id,
                                                text: `${nearbyPort.name} (${nearbyPort.country_name || "Unknown"})`,
                                              }));
                                            return (
                                              <div
                                                key={`${port.port_of_call_id}-${event.id || `draft-${eventIndex}`}`}
                                                style={templateEventCardStyle}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    marginBottom: "12px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  <Label
                                                    style={{
                                                      borderRadius: "999px",
                                                      background: event.placement === "segment" ? "#fff7ed" : "#ecfeff",
                                                      color: event.placement === "segment" ? "#c2410c" : "#0f766e",
                                                    }}
                                                  >
                                                    {event.placement === "segment" ? "Leg event" : "Port event"}
                                                  </Label>
                                                  <Button
                                                    type="button"
                                                    icon="trash"
                                                    color="red"
                                                    size="tiny"
                                                    onClick={() => removeEventFromTemplatePort(index, eventIndex)}
                                                    disabled={isSavingTemplate}
                                                  />
                                                </div>

                                                <div style={{ display: "grid", gap: "12px" }}>
                                                  <Form.Field style={{ margin: 0 }}>
                                                    <label style={fieldLabelStyle}>Event name</label>
                                                    <input
                                                      value={event.name}
                                                      onChange={(e) =>
                                                        updateTemplateEventField(index, eventIndex, "name", e.target.value)
                                                      }
                                                      style={textInputStyle}
                                                    />
                                                  </Form.Field>

                                                  <Form.Field style={{ margin: 0 }}>
                                                    <label style={fieldLabelStyle}>Description</label>
                                                    <textarea
                                                      value={event.description}
                                                      onChange={(e) =>
                                                        updateTemplateEventField(index, eventIndex, "description", e.target.value)
                                                      }
                                                      style={{ ...textAreaStyle, minHeight: "84px" }}
                                                    />
                                                  </Form.Field>

                                                  <div
                                                    style={{
                                                      display: "grid",
                                                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                                                      gap: "12px",
                                                    }}
                                                  >
                                                    <Form.Field style={{ margin: 0 }}>
                                                      <label style={fieldLabelStyle}>Placement</label>
                                                      <Select
                                                        fluid
                                                        value={event.placement}
                                                        options={[
                                                          { key: "port", value: "port", text: "At this port" },
                                                          { key: "segment", value: "segment", text: "On next leg" },
                                                        ]}
                                                        onChange={(_e, data) =>
                                                          updateTemplateEventField(index, eventIndex, "placement", data.value)
                                                        }
                                                        style={{ ...textInputStyle, padding: 0 }}
                                                      />
                                                    </Form.Field>

                                                    <Form.Field style={{ margin: 0 }}>
                                                      <label style={fieldLabelStyle}>Day organised</label>
                                                      <input
                                                        type="number"
                                                        min="1"
                                                        max={templateForm.duration || undefined}
                                                        value={event.day_number}
                                                        placeholder={getTemplateEventDefaultDay(port, event.placement) || "Day"}
                                                        onChange={(e) =>
                                                          updateTemplateEventField(index, eventIndex, "day_number", e.target.value)
                                                        }
                                                        style={textInputStyle}
                                                      />
                                                    </Form.Field>

                                                    <Form.Field style={{ margin: 0 }}>
                                                      <label style={fieldLabelStyle}>Duration</label>
                                                      <input
                                                        type="time"
                                                        value={event.duration}
                                                        onChange={(e) =>
                                                          updateTemplateEventField(index, eventIndex, "duration", e.target.value)
                                                        }
                                                        style={textInputStyle}
                                                      />
                                                    </Form.Field>
                                                  </div>

                                                  {event.placement === "segment" ? (
                                                    <div
                                                      style={{
                                                        display: "grid",
                                                        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                                                        gap: "12px",
                                                      }}
                                                    >
                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>Segment target</label>
                                                        <input
                                                          value={nextPort?.port_of_call?.name ? `Next port: ${nextPort.port_of_call.name}` : "No next port available"}
                                                          readOnly
                                                          style={{ ...textInputStyle, background: "#f8fafc", color: "#475569" }}
                                                        />
                                                      </Form.Field>

                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>Leg behavior</label>
                                                        <Select
                                                          fluid
                                                          value={event.segment_route_behavior}
                                                          options={[
                                                            { key: "waypoint", value: "waypoint", text: "Waypoint on main path" },
                                                            { key: "separate", value: "separate", text: "Separate start to event to end" },
                                                          ]}
                                                          onChange={(_e, data) =>
                                                            updateTemplateEventField(index, eventIndex, "segment_route_behavior", data.value)
                                                          }
                                                          style={{ ...textInputStyle, padding: 0 }}
                                                        />
                                                      </Form.Field>
                                                    </div>
                                                  ) : null}

                                                  <div
                                                    style={{
                                                      display: "grid",
                                                      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                                                      gap: "12px",
                                                    }}
                                                  >
                                                    <Form.Field style={{ margin: 0 }}>
                                                      <label style={fieldLabelStyle}>Transport mode</label>
                                                      <Select
                                                        fluid
                                                        clearable
                                                        value={event.route_mode || null}
                                                        options={TEMPLATE_EVENT_ROUTE_OPTIONS}
                                                        onChange={(_e, data) =>
                                                          updateTemplateEventField(index, eventIndex, "route_mode", data.value || "")
                                                        }
                                                        style={{ ...textInputStyle, padding: 0 }}
                                                      />
                                                    </Form.Field>

                                                    <Form.Field style={{ margin: 0 }}>
                                                      <label style={fieldLabelStyle}>Location mode</label>
                                                      <Select
                                                        fluid
                                                        value={event.location_mode}
                                                        options={TEMPLATE_EVENT_LOCATION_MODE_OPTIONS}
                                                        onChange={(_e, data) => {
                                                          const nextMode = data.value;
                                                          if (
                                                            nextMode !== "custom" &&
                                                            activeTemplateEventMapKey === templateEventKey(index, eventIndex)
                                                          ) {
                                                            mapCtx?.tempPoint?.remove?.();
                                                            setActiveTemplateEventMapKey(null);
                                                          }
                                                          updateTemplateEventField(index, eventIndex, "location_mode", nextMode);
                                                        }}
                                                        style={{ ...textInputStyle, padding: 0 }}
                                                      />
                                                    </Form.Field>
                                                  </div>

                                                  {event.location_mode === "nearby_port" ? (
                                                    <>
                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>Nearby city / port</label>
                                                        <Select
                                                          fluid
                                                          search
                                                          value={event.nearby_port_of_call_id}
                                                          options={nearbyPortOptions}
                                                          onChange={(_e, data) =>
                                                            updateTemplateEventNearbyPort(index, eventIndex, data.value)
                                                          }
                                                          style={{ ...textInputStyle, padding: 0 }}
                                                        />
                                                      </Form.Field>
                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>Event country</label>
                                                        <Select
                                                          fluid
                                                          search
                                                          clearable
                                                          placeholder="Select event country"
                                                          value={event.location_country_iso3 || null}
                                                          options={countryOptions}
                                                          onChange={(_e, data) =>
                                                            updateTemplateEventField(index, eventIndex, "location_country_iso3", data.value || "")
                                                          }
                                                          style={{ ...textInputStyle, padding: 0 }}
                                                        />
                                                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#64748b" }}>
                                                          Defaults from the selected city/port and can be overridden.
                                                        </div>
                                                      </Form.Field>
                                                      <Form.Field style={{ margin: 0 }}>
                                                        <Checkbox
                                                          label="Show this destination as a port-style card in guest cruise overview"
                                                          checked={Boolean(event.display_as_port_card)}
                                                          onChange={(_e, data) =>
                                                            updateTemplateEventField(index, eventIndex, "display_as_port_card", Boolean(data.checked))
                                                          }
                                                        />
                                                      </Form.Field>
                                                    </>
                                                  ) : null}

                                                  {event.location_mode === "custom" ? (
                                                    <>
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "space-between",
                                                          gap: "10px",
                                                          flexWrap: "wrap",
                                                          borderRadius: "12px",
                                                          background: "#eef6ff",
                                                          color: "#33516f",
                                                          padding: "10px 12px",
                                                          fontSize: "0.9rem",
                                                        }}
                                                      >
                                                        <span>
                                                          Pick a point on the map, then drag the marker if needed.
                                                        </span>
                                                        <Button
                                                          type="button"
                                                          size="tiny"
                                                          icon="map marker alternate"
                                                          content={
                                                            activeTemplateEventMapKey === templateEventKey(index, eventIndex)
                                                              ? "Marker active"
                                                              : "Pick on map"
                                                          }
                                                          color={
                                                            activeTemplateEventMapKey === templateEventKey(index, eventIndex)
                                                              ? "blue"
                                                              : undefined
                                                          }
                                                          basic={activeTemplateEventMapKey !== templateEventKey(index, eventIndex)}
                                                          onClick={() => startTemplateEventMapLocation(index, eventIndex)}
                                                          disabled={isSavingTemplate}
                                                        />
                                                      </div>

                                                      <div
                                                        style={{
                                                          display: "grid",
                                                          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                                                          gap: "12px",
                                                        }}
                                                      >
                                                        <Form.Field style={{ margin: 0 }}>
                                                          <label style={fieldLabelStyle}>Longitude</label>
                                                          <input
                                                            type="number"
                                                            step="any"
                                                            value={event.custom_location?.coordinates?.[0] ?? ""}
                                                            onChange={(e) =>
                                                              updateTemplateEventCoordinate(index, eventIndex, "lon", e.target.value)
                                                            }
                                                            onBlur={() => {
                                                              const lon = event.custom_location?.coordinates?.[0];
                                                              const lat = event.custom_location?.coordinates?.[1];
                                                              lookupTemplateEventCountry(index, eventIndex, lon, lat);
                                                            }}
                                                            style={textInputStyle}
                                                          />
                                                        </Form.Field>

                                                        <Form.Field style={{ margin: 0 }}>
                                                          <label style={fieldLabelStyle}>Latitude</label>
                                                          <input
                                                            type="number"
                                                            step="any"
                                                            value={event.custom_location?.coordinates?.[1] ?? ""}
                                                            onChange={(e) =>
                                                              updateTemplateEventCoordinate(index, eventIndex, "lat", e.target.value)
                                                            }
                                                            onBlur={() => {
                                                              const lon = event.custom_location?.coordinates?.[0];
                                                              const lat = event.custom_location?.coordinates?.[1];
                                                              lookupTemplateEventCountry(index, eventIndex, lon, lat);
                                                            }}
                                                            style={textInputStyle}
                                                          />
                                                        </Form.Field>
                                                      </div>

                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>Location label</label>
                                                        <input
                                                          value={event.custom_location_label}
                                                          onChange={(e) =>
                                                            updateTemplateEventField(index, eventIndex, "custom_location_label", e.target.value)
                                                          }
                                                          style={textInputStyle}
                                                        />
                                                      </Form.Field>

                                                      <Form.Field style={{ margin: 0 }}>
                                                        <label style={fieldLabelStyle}>
                                                          Event country
                                                          {templateEventCountryLookupKey === templateEventKey(index, eventIndex) ? (
                                                            <Loader active inline size="tiny" style={{ marginLeft: "8px" }} />
                                                          ) : null}
                                                        </label>
                                                        <Select
                                                          fluid
                                                          search
                                                          clearable
                                                          loading={templateEventCountryLookupKey === templateEventKey(index, eventIndex)}
                                                          placeholder="Select event country"
                                                          value={event.location_country_iso3 || null}
                                                          options={countryOptions}
                                                          onChange={(_e, data) =>
                                                            updateTemplateEventField(index, eventIndex, "location_country_iso3", data.value || "")
                                                          }
                                                          style={{ ...textInputStyle, padding: 0 }}
                                                        />
                                                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#64748b" }}>
                                                          Used for holiday checks. Coordinate lookup fills this automatically, but it can be overridden.
                                                        </div>
                                                      </Form.Field>
                                                    </>
                                                  ) : null}

                                                  {event.location_mode === "port" ? (
                                                    <div
                                                      style={{
                                                        borderRadius: "12px",
                                                        background: "#f8fafc",
                                                        color: "#475569",
                                                        padding: "10px 12px",
                                                        fontSize: "0.9rem",
                                                      }}
                                                    >
                                                      Uses the coordinates of {port.port_of_call?.name}.
                                                    </div>
                                                  ) : null}

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      justifyContent: "flex-end",
                                                      paddingTop: "12px",
                                                      borderTop: "1px solid #e2e8f0",
                                                    }}
                                                  >
                                                    <Button
                                                      type="button"
                                                      size="small"
                                                      icon="save"
                                                      content={templateForm.id ? "Save Event" : "Create Template"}
                                                      onClick={saveTemplate}
                                                      loading={isSavingTemplate}
                                                      disabled={isSavingTemplate}
                                                      style={templatePrimaryButtonStyle}
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              ...templateSectionStyle,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div>
                              <Header as="h4" style={templateSectionTitleStyle}>
                                Ready to save
                              </Header>
                              <p style={templateSectionCopyStyle}>
                                Save the template once the route and schedule look correct.
                              </p>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              {templateForm.id ? (
                                <Button
                                  type="button"
                                  icon="map outline"
                                  content={templateForm.tour_map?.id ? "Edit Template Map" : "Create Template Map"}
                                  onClick={openOrCreateTemplateMap}
                                  loading={isLoadingTemplateMap}
                                  disabled={isSavingTemplate || isLoadingTemplateMap}
                                  style={templateSecondaryButtonStyle}
                                />
                              ) : null}
                              <Button
                                type="button"
                                icon="save"
                                content={templateForm.id ? "Save Template" : "Create Template"}
                                onClick={saveTemplate}
                                loading={isSavingTemplate}
                                disabled={isSavingTemplate}
                                style={templatePrimaryButtonStyle}
                              />
                              <Button
                                type="button"
                                icon="undo"
                                content="Clear"
                                onClick={resetTemplateForm}
                                disabled={isSavingTemplate}
                                style={templateSecondaryButtonStyle}
                              />
                            </div>
                          </div>
                        </Form>
                      </div>
                    )}
                  </div>
                </Dimmer.Dimmable>
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>

      {/* Flight Tracking Modal */}
      <Modal
        open={flightTrackingModalOpen}
        onClose={() => setFlightTrackingModalOpen(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="plane" />
          Track Flight Prices for {selectedTourForTracking?.name}
        </Modal.Header>
        <Modal.Content scrolling>
          {trackingMessage && (
            <Message positive icon>
              <Icon name="check circle" />
              <Message.Content>{trackingMessage}</Message.Content>
            </Message>
          )}

          {trackingError && (
            <Message negative icon>
              <Icon name="exclamation triangle" />
              <Message.Content>{trackingError}</Message.Content>
            </Message>
          )}

          {!homeAirport && (
            <Message warning icon>
              <Icon name="warning sign" />
              <Message.Content>
                <Message.Header>No Home Airport Set</Message.Header>
                Please set your home airport in user preferences to enable flight tracking.
              </Message.Content>
            </Message>
          )}

          {homeAirport && (
            <Form loading={loadingAirports}>
              <Segment>
                <Header as="h4">
                  <Icon name="home" />
                  Home Airport
                </Header>
                <Label size="large" color="blue">
                  <Icon name="plane" />
                  {homeAirport.code} - {homeAirport.name}
                </Label>
              </Segment>

              <Divider />

              <Segment>
                <Header as="h4">
                  <Icon name="arrow right" />
                  Outbound Flight (to Tour Start)
                </Header>
                <p>Select destination airport(s) near the first port:</p>
                {outboundAirports.length > 0 ? (
                  <Form.Group grouped>
                    {outboundAirports.map((airport) => (
                      <Form.Checkbox
                        key={airport.code}
                        label={
                          <label>
                            <strong>{airport.code}</strong> - {airport.name}
                            <span style={{ color: 'gray', marginLeft: '10px' }}>
                              ({airport.distance_km} km away)
                            </span>
                          </label>
                        }
                        checked={selectedOutboundAirports.includes(airport.code)}
                        onChange={(_e, { checked }) => {
                          if (checked) {
                            setSelectedOutboundAirports([...selectedOutboundAirports, airport.code]);
                          } else {
                            setSelectedOutboundAirports(selectedOutboundAirports.filter(c => c !== airport.code));
                          }
                        }}
                      />
                    ))}
                  </Form.Group>
                ) : (
                  <Message info>No airports found near the first port.</Message>
                )}
              </Segment>

              <Divider />

              <Segment>
                <Header as="h4">
                  <Icon name="arrow left" />
                  Return Flight (from Tour End)
                </Header>
                <p>Select departure airport(s) near the last port:</p>
                {returnAirports.length > 0 ? (
                  <Form.Group grouped>
                    {returnAirports.map((airport) => (
                      <Form.Checkbox
                        key={airport.code}
                        label={
                          <label>
                            <strong>{airport.code}</strong> - {airport.name}
                            <span style={{ color: 'gray', marginLeft: '10px' }}>
                              ({airport.distance_km} km away)
                            </span>
                          </label>
                        }
                        checked={selectedReturnAirports.includes(airport.code)}
                        onChange={(_e, { checked }) => {
                          if (checked) {
                            setSelectedReturnAirports([...selectedReturnAirports, airport.code]);
                          } else {
                            setSelectedReturnAirports(selectedReturnAirports.filter(c => c !== airport.code));
                          }
                        }}
                      />
                    ))}
                  </Form.Group>
                ) : (
                  <Message info>No airports found near the last port.</Message>
                )}
              </Segment>
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setFlightTrackingModalOpen(false)}>
            <Icon name="cancel" />
            Cancel
          </Button>
          <Button
            primary
            onClick={setupFlightTracking}
            disabled={!homeAirport || loadingAirports || (selectedOutboundAirports.length === 0 && selectedReturnAirports.length === 0)}
          >
            <Icon name="checkmark" />
            Start Tracking
          </Button>
        </Modal.Actions>
      </Modal>
    </SidebarWrapper>
  );
}
